import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import Papa from 'papaparse';
import {
  Alert,
  Button,
  Checklist,
  Field,
  FileField,
  Select,
  Loader,
  Modal,
  Table
} from '@itds/elements/index.js';
import { STATES } from '@itds/Constants';

import Api from '@carrier/utils/Api';

const Import = ({ version }) => {
  const [data, setData] = useState({
    file: null,
    format: 'row_data',
    rates_json: '',
    states: []
  });
  const [show_example, toggleExample] = useState(false);
  const [file_errors, setFileErrors] = useState(['placeholder']);
  const [error_msg, setErrorMsg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);

  const handleFormatChange = e => {
    setData({ ...data, format: e.target.value, file: null });
    setErrorMsg(null);
    setFileErrors(['placeholder']);
  };

  const handleFileChange = file => {
    if (file) {
      Papa.parse(file, {
        header: data.format === 'row_data',
        skipEmptyLines: true,
        complete: results => {
          const file_errors = [];
          const updated_data = {};

          let error_msg = null;

          if (results.errors.length) {
            error_msg =
              'Could not parse the selected file. Please check the file format and try again.';
          } else {
            if (data.format === 'grid_data') {
              const rows = results.data;

              if (rows[0][0] !== 'Product') {
                file_errors.push('Product');
              }
              if (rows[1][0] !== 'Gender') {
                file_errors.push('Gender');
              }
              if (rows[2][0] !== 'Band') {
                file_errors.push('Band');
              }
              if (rows[3][0] !== 'Health Category') {
                file_errors.push('Health Category');
              }
              if (rows[4][0] !== 'Modal Factor') {
                file_errors.push('Modal Factor');
              }
              if (!rows[5]) {
                file_errors.push('At least 1 age row');
              }

              if (file_errors.length) {
                error_msg = 'CSV is missing the following rows: ';
              }
            } else {
              const required_column_headers = [
                'product_id',
                'band_id',
                'modal_factor_id',
                'gender',
                'age'
              ];

              required_column_headers.forEach(h => {
                if (!results.meta.fields.includes(h)) {
                  file_errors.push(h);
                }
              });
            }

            if (file_errors.length || error_msg) {
              error_msg = 'CSV is missing the following headers: ';
              updated_data.file = null;
              updated_data.rates_json = '';
            } else {
              updated_data.file = file;
              updated_data.rates_json = JSON.stringify(results.data);
            }

            setData({ ...data, ...updated_data });
          }

          setErrorMsg(error_msg);
          setFileErrors(file_errors);
        }
      });
    }
  };

  const handleStatesChange = states => {
    setData({ ...data, states });
  };

  const startImport = () => {
    const { format, rates_json, states } = data;

    setLoading(true);

    Api.importRates({
      csv_format: format,
      carrier_id: version.carrier_id,
      data_version_id: version.id,
      email: Cookies.get('email'),
      rates_json,
      states
    }).then(() => {
      window.scrollTo(0, 0);
      setData({
        file: null,
        format: 'row_data',
        rates_json: '',
        states: []
      });
      setLoading(false);
      setStarted(true);

      setTimeout(() => {
        setStarted(false);
      }, 4000);
    });
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      {started ? (
        <Alert type='success'>
          Your import has been started and will run behind the scenes. You will be notified via
          email once it's been completed.
        </Alert>
      ) : null}

      {show_example ? (
        <Modal fullScreen={true} onClose={toggleExample.bind(null, false)} title='Format Details'>
          <div style={{ padding: 30 }}>
            {data.format === 'row_data' ? (
              <>
                <p>
                  The CSV format will have all the data for a given rate is defined on a single row.
                  Below is a description of the different columns headers and their values:
                </p>
                <Table
                  columns={[
                    {
                      id: 'product_id',
                      label: 'product_id',
                      width: 250,
                      styles: { verticalAlign: 'top' }
                    },
                    {
                      id: 'band_id',
                      label: 'band_id',
                      width: 250,
                      styles: { verticalAlign: 'top' }
                    },
                    {
                      id: 'modal_factor_id',
                      label: 'modal_factor_id',
                      width: 250,
                      styles: { verticalAlign: 'top' }
                    },
                    {
                      id: 'preferred_plus-non_tobacco',
                      label: 'preferred_plus-non_tobacco',
                      width: 250,
                      styles: { verticalAlign: 'top' }
                    },
                    {
                      id: 'preferred_plus-tobacco',
                      label: 'preferred_plus-tobacco',
                      width: 250,
                      styles: { verticalAlign: 'top' }
                    },
                    {
                      id: 'preferred-non_tobacco',
                      label: 'preferred-non_tobacco',
                      width: 250,
                      styles: { verticalAlign: 'top' }
                    },
                    {
                      id: 'preferred-tobacco',
                      label: 'preferred-tobacco',
                      width: 250,
                      styles: { verticalAlign: 'top' }
                    },
                    {
                      id: 'standard_plus-non_tobacco',
                      label: 'standard_plus-non_tobacco',
                      width: 250,
                      styles: { verticalAlign: 'top' }
                    },
                    {
                      id: 'standard_plus-tobacco',
                      label: 'standard_plus-tobacco',
                      width: 250,
                      styles: { verticalAlign: 'top' }
                    },
                    {
                      id: 'standard-non_tobacco',
                      label: 'standard-non_tobacco',
                      width: 250,
                      styles: { verticalAlign: 'top' }
                    },
                    {
                      id: 'standard-tobacco',
                      label: 'standard-tobacco',
                      width: 250,
                      styles: { verticalAlign: 'top' }
                    },
                    { id: 'gender', label: 'gender', width: 250, styles: { verticalAlign: 'top' } },
                    {
                      id: 'payment_mode',
                      label: 'payment_mode',
                      width: 250,
                      styles: { verticalAlign: 'top' }
                    },
                    { id: 'age', label: 'age', width: 250, styles: { verticalAlign: 'top' } }
                  ]}
                  data={[
                    {
                      product_id: "The Product ID that is associated with this row's rates.",
                      band_id: "The Band ID that is associated with this row's rates.",
                      modal_factor_id:
                        'The rate for this health category + tobacco combination. Omit this column if the carrier does not offer this combo.',
                      'preferred_plus-non_tobacco':
                        'The rate for this health category + tobacco combination. Omit this column if the carrier does not offer this combo.',
                      'preferred_plus-tobacco':
                        'The rate for this health category + tobacco combination. Omit this column if the carrier does not offer this combo.',
                      'preferred-non_tobacco':
                        'The rate for this health category + tobacco combination. Omit this column if the carrier does not offer this combo.',
                      'preferred-tobacco':
                        'The rate for this health category + tobacco combination. Omit this column if the carrier does not offer this combo.',
                      'standard_plus-non_tobacco':
                        'The rate for this health category + tobacco combination. Omit this column if the carrier does not offer this combo.',
                      'standard_plus-tobacco':
                        'The rate for this health category + tobacco combination. Omit this column if the carrier does not offer this combo.',
                      'standard-non_tobacco':
                        'The rate for this health category + tobacco combination. Omit this column if the carrier does not offer this combo.',
                      'standard-tobacco':
                        'The rate for this health category + tobacco combination. Omit this column if the carrier does not offer this combo.',
                      gender:
                        'The gender that is associated with this row\'s rates. It must be one of the following values: "Male", "Female".',
                      payment_mode:
                        'The payment mode that is associated with this row\'s rates. It must be one of the following vlaues: "monthly", "quarterly", "semi_annual", "annual". If you leave this blank then ALL payment modes will be applied.',
                      age: "The age that is associated with this row's rates."
                    }
                  ]}
                  tableLayout='fixed'
                />
              </>
            ) : (
              <>
                <p>
                  This CSV format will contain a "lookup grid" to find a specific rate for a given
                  age and criteria combination. It will have the ages in the first column.
                  Subsequent columns will contain the rates for a sepcific combination of product,
                  gender, band, and health category. Values for product, gender, band, and health
                  category will be carrier specific and defined at the top of each column. Below is
                  an example structure using values from Legal & General.
                </p>
                <Table
                  columns={[
                    { id: 'exampleName', label: ' ' },
                    { id: 'value', label: '' },
                    { id: 'value2', label: '' }
                  ]}
                  data={[
                    {
                      exampleName: 'Product',
                      value: 157,
                      value2: 157
                    },
                    {
                      exampleName: 'Gender',
                      value: 'Male',
                      value2: 'Female'
                    },
                    {
                      exampleName: 'Band',
                      value: 6962,
                      value2: 6962
                    },
                    {
                      exampleName: 'Health Category',
                      value: 3326,
                      value2: 3326
                    },
                    {
                      exampleName: 'Modal Factor',
                      value: 1513,
                      value2: 1513
                    },
                    {
                      exampleName: 20,
                      value: 0.3499,
                      value2: 0.4822
                    },
                    {
                      exampleName: 21,
                      value: 0.3499,
                      value2: 0.4822
                    }
                  ]}
                />
              </>
            )}
          </div>
        </Modal>
      ) : null}

      {error_msg ? (
        <Alert type='error'>
          <div>{error_msg}</div>
          <ul style={{ listStyleType: 'disc', marginLeft: 30, marginTop: 10 }}>
            {file_errors.map((error, i) => {
              return <li key={i}>{error}</li>;
            })}
          </ul>
        </Alert>
      ) : null}

      <Field
        action={{ label: 'View Format Details', onClick: toggleExample.bind(null, true) }}
        label='CSV Format'
      >
        <Select
          onChange={handleFormatChange}
          options={[
            { value: 'row_data', label: 'Row-based Data' },
            { value: 'grid_data', label: 'Lookup Grid' }
          ]}
          value={data.format}
        />
      </Field>

      <Field label='Select CSV File To Import'>
        <FileField file={data.file} invalid={false} onChange={handleFileChange} />
      </Field>

      <Checklist
        columns={4}
        onChange={handleStatesChange}
        options={STATES}
        selected={data.states}
        showSelectAll={true}
      />

      <Button
        disabled={!!error_msg || !!file_errors.length}
        inline={true}
        onClick={startImport}
        style={{ marginTop: 30 }}
      >
        Start Import
      </Button>
    </>
  );
};

Import.propTypes = {
  version: PropTypes.object
};

export default Import;
